import axios from "axios";
import MapPlugin from "../MapPlugin";
import Route from "../../../../../common/Route";
import { GeoJsonPoint } from "../../../../../common/maphelpers";
import { useTranslation } from "react-i18next";
import { useOperationsMap } from "../../OperationsMapProvider";
import LayerSettings from "../../common/LayerSettings";
import { Typography, Stack, Button } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useStation } from "../../../../../providers/StationProvider";
import { NavLink } from "react-router-dom";



export default class AssistNetworkLayer extends MapPlugin {

  constructor(map) {
    super("network", map);
    this.defaultSettings = {};

    this.addImage("business");
  }


  async onMount() {
    try {

      const res = await axios(
        "/api/station/network/list",
        {
          headers: { Authorization: `Bearer ${await this.getAccessToken()}` },
        }
      );

      const p = this.theme.palette;

      this._map.addSource("stations_source", {
        type: "geojson",
        cluster: true,
        clusterRadius: 35,
        data: Route.GeoJson(
          res.data?.filter(s => s?.location?.coordinates?.length === 2 && s.id !== this.station.id)?.map(s => GeoJsonPoint(s.location.coordinates, {
            stationId: s.id,
            displayName: s.displayName,
            icon: "station",
            color: p.info.main
          }))
        )
      });

      // Layer data

      this._map.addLayer({
        id: "stations_background",
        source: "stations_source",
        type: "circle",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-radius": 20, // Adjust the radius as needed
          "circle-color": ["get", "color"], // Set the desired color
          "circle-stroke-width": 3,
          "circle-stroke-color": "#FFF",
        }
      });

      this._map.addLayer({
        id: "stations",
        source: "stations_source",
        type: "symbol",
        filter: ["!", ["has", "point_count"]],
        "layout": {
          "icon-image": ["get", "icon"], // Assume you have an icon named "road-works" added to the style
          "icon-size": 1.8,
          "icon-ignore-placement": true,
        },
        "paint": {
          "icon-color": "#FFF"
        }
      });

      this._map.addLayer({
        id: "stations_text",
        source: "stations_source",
        slot: "elevation-2",
        filter: ["!", ["has", "point_count"]],
        type: "symbol",
        layout: {
          "text-allow-overlap": true,
          "text-ignore-placement": true,
          "text-field": ["get", "displayName"],
          "text-font": [
            "Open Sans Semibold",
            "Arial Unicode MS Bold"
          ],
          "text-size": 16,
          "text-offset": [-0.1, -1.55],
          "text-anchor": "bottom",
          "text-max-width": 7
        },
        paint: {
          "text-halo-color": "#fff",
          "text-halo-width": 2
        }
      });


      // Add cluster layers
      this._map.addLayer({
        id: "stations_cluster_circles",
        source: "stations_source",
        type: "circle",
        filter: ["has", "point_count"],
        paint: {
          "circle-stroke-width": 3,
          "circle-stroke-color": "#FFF",
          "circle-color": [
            "step",
            ["get", "point_count"],
            p.info.light,
            4,
            p.info.main,
            9,
            p.info.dark,
          ],
          "circle-radius": [
            "step",
            ["get", "point_count"],
            18,
            100,
            30,
            750,
            40
          ]
        }
      });

      this._map.addLayer({
        id: "stations_cluster_text",
        source: "stations_source",
        type: "symbol",
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": ["Arial Unicode MS Bold"],
          "text-size": 16,
        },
        paint: {
          "text-color": "#FFF"
        }
      });



    } catch (err) {
      console.log(err);
    }
  }

  onUnMount() {
    this._map.removeLayer("stations");
    this._map.removeLayer("stations_background");
    this._map.removeLayer("stations_text");
    this._map.removeLayer("stations_cluster_circles");
    this._map.removeLayer("stations_cluster_text");
    this._map.removeSource("stations_source");
  }
}


export function AssistNetworkLayerSettings() {
  const { t } = useTranslation();
  const { operationsMap } = useOperationsMap();
  const settings = operationsMap.getSettings("network");
  const [active, setActive] = useState(operationsMap.isPluginActive("network"));
  const { station } = useStation();
  
  useEffect(() => {
    const fn = () => {
      setActive(operationsMap.isPluginActive("network"));
    };

    operationsMap.on("reset", fn);
    return () => operationsMap.off("reset", fn);
  }, [operationsMap, settings]);

  // Handle plugin active state
  const onHandlePlugin = useCallback((ev, val) => {
    setActive(val);

    if (val) {
      operationsMap.activatePlugin("network");
    } else {
      operationsMap.deactivatePlugin("network");
    }
  }, [operationsMap]);

  return (
    <LayerSettings title={t("operationsMap:layers:network:title")} open={active} onChange={onHandlePlugin}>
      <Stack spacing={2}>
        <Typography variant="body2">{t("operationsMap:layers:network:description")}</Typography>

        <Typography variant="body2" color={station?.isNetworkMember ? "success" : "warning"} fontWeight={500}>
          {station?.isNetworkMember ? t("operationsMap:layers:network:isMember") : t("operationsMap:layers:network:notMember")}
        </Typography>

        {!station?.isNetworkMember ? (
          <NavLink to="/network/assist" style={{ textDecoration: "none" }}>
            <Button color="inherit" variant="contained" sx={{ color: "text.primary" }} size="small">
              {t("operationsMap:layers:network:joinNow")}
            </Button>
          </NavLink>
        ) : null}

      </Stack>
    </LayerSettings>
  );
}

